/**
 * Javascript Components Loader
 *
 * The site uses a javascript component system, where we can start js components based on data attributes placed on html elements.
 * They can come with a set of default settings, which in turn can be overriden with another data attribute:
 *
 * ```html
 * <header class="Nav" data-js="navigation" data-js-settings="{'Hello': 'Saturn'}"></header>
 * ```
 *
 * * To automatically bundle the component, place it in `/Content/Scripts/components/`.
 * * To load the component on demand, place it in `/Content/Scripts/ondemand/`.
 *
 * For more info on the actual components available, please see category `components` in the left bar.
 *
 * @module loadComponents
 * @category utils
 *
 */

const initializedComponents = []

/**
 * Goes through all components in `componentsToLoad` and creates an object of all component names with the component as the value
 *
 * @param {Array} componentsToLoad - Array of `required.context` keys.
 * @returns {Object} components
 */
const populateComponents = (componentsToLoad) => {
    const components = {}
    /** loops through all components from `componentsToLoad` and adds it to the return */
    componentsToLoad.forEach(component => {
        const componentName = component.match(/([^/]+)(?=\.\w+$)/)[0]
        const tempComponent = require(`../Components/${componentName}.js`)
        components[componentName] = tempComponent
    })

    return components
}

/**
 * Goes through each element with data-js attribute and either starts the component, or creates a dynamic import() for it to load a chunk and execute it.
 *
 * @returns {void}
 */
const loadComponents = () => {
    const componentsToLoad = require.context(`../Components/`, true, /\.js$/).keys()
    const componentElements = document.querySelectorAll(`[data-js]`)
    const components = populateComponents(componentsToLoad)

    console.groupCollapsed(`---- COMPONENTS -----`)
    console.time(`COMPONENT LOAD TIMER`)

    /** Go through all elements and get info about module to init */
    Array.prototype.forEach.call(componentElements, el => {
        const componentsToStart = el.dataset.js.split(` `)
        const elementInMap = initializedComponents.find(o => o.element === el) // check if component already initialized on this element

        componentsToStart.forEach((componentToStart) => {
            if (typeof window.disabledComponents !== `undefined` && window.disabledComponents.indexOf(componentToStart) > -1) {
                return
            }

            let alreadyInitialized = false

            if (elementInMap) {
                alreadyInitialized = elementInMap.components.find(o => o.component === componentToStart)
            }

            if (!alreadyInitialized) { // if not initialized
                // get the settings for this particular component
                const componentToStartSettings = el.hasAttribute(`data-js-${componentToStart}`) // does it have named settings object?
                    ? el.getAttribute(`data-js-${componentToStart}`) // it does!
                    : typeof el.dataset.jsSettings !== `undefined` // it did not, so check for generic settings object
                        ? el.dataset.jsSettings // has generic
                        : `{}` // no generic as well so just give back an empty object

                if (components[componentToStart]) { /** If module already bundled and loaded */
                    console.groupCollapsed(`Component %c${componentToStart} %cis already loaded, starting it`, `color:green`, `color:black`, el)
                    console.log(`Using settings object: ${componentToStartSettings}`)
                    console.groupEnd()
                    /**
                     * Each component have an init function that we call, that takes the settings to merge in and the element we are starting it on.
                     * @see Category Components
                     */
                    try {
                        components[componentToStart].init(componentToStartSettings, el)
                    } catch (e) {
                        console.error(`bundled component ${componentToStart} failed init:`, e)
                    }
                } else { /** if not, dynamically import it so webpack automatically creates a chunk. */
                    console.groupCollapsed(`Component %c${componentToStart} %cNOT loaded, let's import and start it.`, `color:red`, `color:black`, el)
                    console.log(`Using settings object: ${componentToStartSettings}`)
                    console.groupEnd()
                    // PATWAK NOTE: This is to get around a bug in babel-eslint: https://github.com/babel/babel-eslint/issues/681
                    import('../Ondemand/' + componentToStart + '.js').then(m => { // eslint-disable-line quotes
                        try {
                            m.init(componentToStartSettings, el)
                        } catch (e) {
                            console.error(`ondemand component ${componentToStart} failed init:`, e)
                        }
                    })
                }

                // add component to element in map
                const _element = initializedComponents.find(o => o.element === el)
                if (_element) {
                    _element.components.push({
                        component: componentToStart
                    })
                } else {
                    initializedComponents.push({
                        element: el,
                        components: [
                            { component: componentToStart }
                        ]
                    })
                }
            }
        })
    })
    console.timeEnd(`COMPONENT LOAD TIMER`)
    console.groupEnd(`---- COMPONENTS -----`)
}

export const initComponents = () => {
    loadComponents()

    jAwn.on(document, `initComponents`, loadComponents)
}
